import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PublicNavbar from '../Layout/PublicNavbar';
import Navbar from '../Layout/Navbar';
import SEO from '../Common/SEO';
import schwabLogo from '../../assets/logos/schwab.svg';
import fidelityLogo from '../../assets/logos/fidelity.svg';
import etradeLogo from '../../assets/logos/etrade.svg';
import robinhoodLogo from '../../assets/logos/robinhood.png';
import ibkrLogo from '../../assets/logos/ibkr.svg';
import tastyTradeLogo from '../../assets/logos/tastytrade.svg';
import './Home.css';

function Home() {
  const { isAuthenticated, loading } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);

  const carouselSlides = [
    {
      image: '/dashboard-preview-1.jpg',
      alt: 'P&L overview with allocation chart and position details',
      caption: 'P&L Overview & Portfolio Allocation'
    },
    {
      image: '/dashboard-preview-2.jpg',
      alt: 'Last 7 trading days P&L chart and monthly returns',
      caption: 'Performance Charts & Monthly Returns'
    },
    {
      image: '/dashboard-preview-3.jpg',
      alt: 'Strategy and ticker performance tables',
      caption: 'Strategy & Ticker Performance'
    },
    {
      image: '/dashboard-preview-4.jpg',
      alt: 'Trade list with status and actions',
      caption: 'Complete Trade History'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [carouselSlides.length]);

  const features = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 19h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M7 16V9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M12 16V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M17 16v-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
      ),
      title: 'Complete Wheel Strategy Tracking',
      description: 'Track the full CSP → Assignment → Covered Call cycle with LEAPS support. Auto-creates stock positions with correct cost basis when assigned—nothing falls through the cracks.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 7h16v10H4z" stroke="currentColor" strokeWidth="1.6"/>
          <path d="M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6"/>
        </svg>
      ),
      title: 'Advanced Statistics & Analytics',
      description: 'Go beyond basic P&L. Monthly breakdown with trend indicators, profit factor, expected value, premium income, and a sortable ticker-by-strategy matrix. Plus risk-adjusted metrics: Sharpe ratio, Sortino ratio, max drawdown, and Calmar ratio. See exactly where your edge is.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6"/>
          <path d="M7 2v4M17 2v4M3 10h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <circle cx="8" cy="14" r="1.5" fill="currentColor"/>
          <circle cx="12" cy="14" r="1.5" fill="currentColor"/>
          <circle cx="16" cy="14" r="1.5" fill="currentColor"/>
        </svg>
      ),
      title: 'Smart Expiration Calendar',
      description: 'Never miss an expiration or earnings date. DTE urgency colors, ITM/OTM status, and actionable roll/close suggestions keep you ahead of the market.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M3 10h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M5 10v8M9 10v8M15 10v8M19 10v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M12 4l9 6H3l9-6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Multi-Account Management',
      description: 'Track IRA, Roth IRA, and taxable accounts separately with deposits, withdrawals, and account-specific P/L. View individual accounts or combined portfolio performance.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
          <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 18v-6M9 15l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Import Directly from Your Brokerage',
      description: 'Upload your CSV from Schwab, Fidelity, Tastytrade, IBKR, E*Trade, or Robinhood and we auto-detect the format. Assignments, expirations, and trade matching are handled automatically. Or use our template for full control.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 12h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M8 7l-4 5 4 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 7l4 5-4 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Full Spread Strategy Support',
      description: 'Track vertical spreads (Bull Put, Bear Call, Bull Call, Bear Put), multi-leg spreads (Iron Condor, Iron Butterfly), and synthetic positions (Synthetic Long, Synthetic Short, Risk Reversal)—with net credit/debit, max gain, max loss, and break-even all calculated automatically. Spreads in your brokerage file are detected and grouped on import.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 3v9l7.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/>
        </svg>
      ),
      title: 'Free Trading Tools',
      description: 'Free calculators anyone can use, no account required: options profit, wheel strategy ROI, and position size. Free accounts also get a VIX-based position sizer and a tax estimator covering federal brackets and all 50 states.'
    }
  ];

  // Show loading while checking auth status
  if (loading) {
    return (
      <>
        {isAuthenticated ? <Navbar /> : <PublicNavbar />}
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={null}
        description="Free options trading tracker for wheel strategy, vertical spreads, covered calls, cash-secured puts, iron condors, and LEAPS. Track performance, analyze returns, and manage multiple accounts. Free forever, upgrade to Pro for unlimited access."
        canonicalPath=""
      />
      {isAuthenticated ? <Navbar /> : <PublicNavbar />}
      <main className="home-container">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-badge">Free forever • No credit card required</div>
            <div className="hero-pricing-note">Upgrade to Pro for $4.99/month</div>
            <h1 className="hero-title">Track Every Options Trade You Make</h1>
            <p className="hero-subtitle">
              Stop using spreadsheets. Track CSPs, covered calls, vertical spreads, iron condors, LEAPS, and more with automatic P&L calculations and performance analytics.
            </p>
            <div className="hero-buttons">
              {isAuthenticated ? (
                <Link to="/dashboard" className="btn btn-primary btn-large">
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/register" className="btn btn-primary btn-large btn-hero-primary">
                    Get Started Free
                  </Link>
                  <Link to="/pricing" className="btn-text-link">
                    See pricing →
                  </Link>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Dashboard Preview Section */}
        <section className="dashboard-preview-section">
          <div className="container">
            <h2 className="section-title">Options Trading Dashboard with Real-Time P&L</h2>
            <div className="dashboard-carousel">
              <div className="carousel-container">
                {carouselSlides.map((slide, index) => (
                  <div
                    key={index}
                    className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                  >
                    <img
                      srcSet={`${slide.image.replace('.jpg', '-small.jpg')} 400w,
                              ${slide.image.replace('.jpg', '-medium.jpg')} 800w,
                              ${slide.image} 1200w`}
                      sizes="(max-width: 480px) 400px, (max-width: 900px) 800px, 900px"
                      src={slide.image}
                      alt={slide.alt}
                      width="900"
                      height="634"
                      className="dashboard-preview-image"
                      loading={index === 0 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : "auto"}
                    />
                  </div>
                ))}
              </div>
              <div className="carousel-dots">
                {carouselSlides.map((slide, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(index)}
                    aria-label={`View ${slide.caption}`}
                  />
                ))}
              </div>
              <p className="carousel-caption">{carouselSlides[currentSlide].caption}</p>
            </div>
          </div>
        </section>

        {/* Why Options Tracker Section */}
        <section className="why-section">
          <div className="container">
            <h2 className="section-title">Why Choose Options Tracker for Wheel Strategy?</h2>
            <div className="why-grid">
              <div className="why-card">
                <div className="why-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3>Wheel Strategy Tracking</h3>
                <p>Track the full CSP → Assignment → Covered Call flow with connected trades and automatic position management.</p>
              </div>
              <div className="why-card">
                <div className="why-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M4 7h16v10H4z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M8 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </div>
                <h3>Automatic P&L & Deep Analytics</h3>
                <p>No more manual spreadsheet formulas. Get instant P&L, profit factor, expected value, premium income, and return metrics.</p>
              </div>
              <div className="why-card">
                <div className="why-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M5 10v8M9 10v8M15 10v8M19 10v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M12 4l9 6H3l9-6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Multi-Account Support</h3>
                <p>Track IRA, taxable, and margin accounts separately with deposits, withdrawals, and combined reporting.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Brokerage Import Highlight Section */}
        <section className="brokerage-import-section">
          <div className="container">
            <h2 className="section-title">Import Trades from Your Brokerage in Seconds</h2>
            <p className="section-subtitle">
              No manual data entry. Upload your transaction history CSV and we handle the rest.
            </p>
            <div className="brokerage-logos-row">
              <div className="brokerage-logo-item">
                <img src={schwabLogo} alt="Charles Schwab" className="brokerage-logo" />
              </div>
              <div className="brokerage-logo-item">
                <img src={fidelityLogo} alt="Fidelity" className="brokerage-logo" />
              </div>
              <div className="brokerage-logo-item">
                <img src={etradeLogo} alt="E*TRADE" className="brokerage-logo" />
              </div>
              <div className="brokerage-logo-item">
                <img src={tastyTradeLogo} alt="tastytrade" className="brokerage-logo" />
              </div>
              <div className="brokerage-logo-item">
                <img src={ibkrLogo} alt="Interactive Brokers" className="brokerage-logo" />
              </div>
              <div className="brokerage-logo-item">
                <img src={robinhoodLogo} alt="Robinhood" className="brokerage-logo" />
              </div>
            </div>
            <div className="brokerage-features-list">
              <div className="brokerage-feature">
                <svg viewBox="0 0 24 24" fill="none" className="check-icon">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Auto-detects your brokerage format</span>
              </div>
              <div className="brokerage-feature">
                <svg viewBox="0 0 24 24" fill="none" className="check-icon">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Matches opening and closing trades</span>
              </div>
              <div className="brokerage-feature">
                <svg viewBox="0 0 24 24" fill="none" className="check-icon">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Handles assignments, expirations, and exercises</span>
              </div>
              <div className="brokerage-feature">
                <svg viewBox="0 0 24 24" fill="none" className="check-icon">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Manual column mapping for unsupported formats</span>
              </div>
              <div className="brokerage-feature">
                <svg viewBox="0 0 24 24" fill="none" className="check-icon">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Groups spread legs into spreads automatically</span>
              </div>
              <div className="brokerage-feature">
                <svg viewBox="0 0 24 24" fill="none" className="check-icon">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Flags trades to review before you import</span>
              </div>
            </div>
          </div>
        </section>

        {/* All Features Section */}
        <section className="features-section">
          <div className="container">
            <h2 className="section-title">Complete Options Trading Features</h2>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works-section">
          <div className="container">
            <h2 className="section-title">How to Track Options Trades in 4 Steps</h2>
            <div className="steps-container">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Create Accounts</h3>
                <p>Set up your trading accounts (IRA, taxable, margin) with initial balances and track deposits/withdrawals.</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Import Your Trades</h3>
                <p>Upload your CSV from Schwab, Fidelity, Tastytrade, IBKR, E*Trade, or Robinhood. We auto-detect the format and import your options trades instantly.</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Analyze Performance</h3>
                <p>Dashboard for today's P&L, daily and monthly returns, plus a dedicated Statistics page with monthly breakdown, profit factor, expected value, premium income, and more.</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h3>Manage & Export</h3>
                <p>Close positions, track partial closes, and export your data anytime for backup or analysis.</p>
              </div>
            </div>
            <div className="steps-cta">
              <Link to="/how-to-use" className="text-link">Read the complete guide →</Link>
            </div>
          </div>
        </section>

        {/* Who Is This For Section */}
        <section className="who-section">
          <div className="container">
            <h2 className="section-title">Who Should Use an Options Trading Tracker?</h2>
            <div className="who-grid">
              <div className="who-card">
                <h3>Wheel Strategy Traders</h3>
                <p>If you sell cash-secured puts, get assigned shares, and then sell covered calls, Options Tracker is built specifically for you. Track the entire wheel cycle from CSP to assignment to covered call with connected trade flows.</p>
              </div>
              <div className="who-card">
                <h3>Income-Focused Investors</h3>
                <p>Generate consistent income from options premium? Track your monthly returns, see which tickers perform best, and analyze your win rate across different strategies. Replace your options trading spreadsheet with a purpose-built tool.</p>
              </div>
              <div className="who-card">
                <h3>LEAPS Traders</h3>
                <p>Long-term options require different tracking. Monitor your LEAPS positions, track unrealized gains, and manage exercises. Perfect for traders using LEAPS as stock replacement or for covered call strategies.</p>
              </div>
              <div className="who-card">
                <h3>Multi-Account Traders</h3>
                <p>Trading in multiple accounts like IRA, Roth IRA, and taxable accounts? Keep them organized with separate tracking, deposits, withdrawals, and combined portfolio views.</p>
              </div>
              <div className="who-card">
                <h3>Spread Traders</h3>
                <p>Trading vertical spreads, iron condors, or synthetic positions? Options Tracker handles multi-leg strategies with automatic net premium, max gain/loss, and break-even calculations. Track credit and debit spreads side by side with your wheel trades.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h3>What is the wheel strategy?</h3>
                <p>The wheel strategy is an options income strategy where you sell cash-secured puts on stocks you want to own. If assigned, you then sell covered calls on those shares until they're called away, then repeat. Options Tracker helps you track this entire cycle. <Link to="/blog/wheel-strategy-tracking-guide" className="text-link">Learn more about the wheel strategy →</Link></p>
              </div>
              <div className="faq-item">
                <h3>Can I import trades from my brokerage?</h3>
                <p>Yes! Just download your transaction history CSV from Schwab, Fidelity, Tastytrade, IBKR, E*Trade, or Robinhood and upload it. We auto-detect the format, parse your options trades, match openings with closings, and handle assignments and expirations automatically.</p>
              </div>
              <div className="faq-item">
                <h3>How is P&L calculated?</h3>
                <p>P&L is calculated automatically based on your entry and exit prices, including commissions. For options, we calculate premium received minus premium paid. For stock assignments, we track cost basis through the full wheel cycle.</p>
              </div>
              <div className="faq-item">
                <h3>Is my data secure?</h3>
                <p>Yes. Your data is encrypted and stored securely. We never share your trading data with third parties. You can export your data anytime for backup.</p>
              </div>
              <div className="faq-item">
                <h3>What's the difference from a spreadsheet?</h3>
                <p>Spreadsheets require manual formulas and break easily. Options Tracker automatically calculates P&L, tracks connected trades (like CSP → assignment → covered call), provides advanced analytics like profit factor and expected value, and handles the complexity of options tracking.</p>
              </div>
              <div className="faq-item">
                <h3>Do you support spread strategies?</h3>
                <p>Yes. Options Tracker supports vertical spreads (Bull Put, Bear Call, Bull Call, Bear Put), multi-leg spreads (Iron Condor, Iron Butterfly), and synthetic spreads (Synthetic Long, Synthetic Short, Risk Reversal). Net premium, max gain, max loss, and break-even are calculated automatically for each spread.</p>
              </div>
              <div className="faq-item">
                <h3>Do you connect to my brokerage?</h3>
                <p>We don't require brokerage credentials. Instead, download your transaction history CSV from your brokerage and upload it to Options Tracker. We support Schwab, Fidelity, Tastytrade, IBKR, E*Trade, and Robinhood formats with automatic detection. Your data stays under your control.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-card">
              <h2>Start tracking your trades today</h2>
              <p className="cta-benefits">Free forever • No credit card required • Pro $4.99/month</p>
              <div className="cta-button-wrapper">
                {isAuthenticated ? (
                  <Link to="/dashboard" className="btn btn-primary btn-large">
                    Go to Dashboard
                  </Link>
                ) : (
                  <Link to="/register" className="btn btn-primary btn-large">
                    Get Started Free
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default Home;

